import React, { useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatInput } from './ChatInput';
import { ChatReport } from './ChatReport';
import { AnalysisLoading } from './AnalysisLoading';
import { MessageSquare, AlertCircle, RotateCcw } from 'lucide-react';
import { useFootyAI } from '../../hooks/useFootyAI';
import { useChatInput } from '../../contexts/ChatContext';
import { useUserHistory } from '../../hooks/useUserHistory';
import { useSearchLimits } from '../../hooks/useSearchLimits';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { ChatHistoryService } from '../../services/chatHistory';
import { SearchMetadata } from '../../types/search';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SignInPrompt } from '../auth/SignInPrompt';

interface ChatSectionProps {
  initialQuery?: string;
  autoSearch?: boolean;
  searchMetadata?: SearchMetadata;
}

export const ChatSection: React.FC<ChatSectionProps> = ({ 
  initialQuery,
  autoSearch = false,
  searchMetadata
}) => {
  const [report, setReport] = React.useState<string | null>(null);
  const [shareId, setShareId] = React.useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = React.useState<string | null>(null);
  const [currentMetadata, setCurrentMetadata] = React.useState<SearchMetadata | undefined>(searchMetadata);
  const [hasAttemptedSearch, setHasAttemptedSearch] = React.useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = React.useState(false);
  const { analyze, isAnalyzing, error } = useFootyAI();
  const { inputValue, setInputValue, deepAnalysis } = useChatInput();
  const { recordSearch } = useUserHistory();
  const { remainingSearches, isLimited, isLoading, refetchLimits } = useSearchLimits();
  const { user } = useAuth();
  const chatSectionRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToChat = useCallback(() => {
    if (chatSectionRef.current) {
      const yOffset = -20;
      const y = chatSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  // Calculate display credits
  const displaySearches = !isLoading ? (
    user ? remainingSearches : // For logged in users, show actual credits
    sessionStorage.getItem('footyAI_sessionId') ? remainingSearches : // For users with session, show remaining credits
    6 // For new users, show initial 6 credits
  ) : null;

  const handleSubmit = useCallback(async (query: string) => {
    if (isAnalyzing) return;

    // Check search limits
    if (isLimited) {
      if (!user) {
        setShowSignInPrompt(true);
        return;
      } else {
        // Show upgrade message for authenticated users
        alert('You have used all your search credits. Upgrade feature coming soon!');
        return;
      }
    }

    try {
      setReport(null);
      setShareId(null);
      setPendingMessage(query);
      setHasAttemptedSearch(true);

      scrollToChat();

      // Record the search if user is logged in
      if (user) {
        await recordSearch(query);
      }
      
      const metadata = autoSearch ? currentMetadata : undefined;
      const response = await analyze(query, deepAnalysis, metadata);
      
      if (response) {
        setReport(response.result);
        setInputValue('');
        
        const newShareId = uuidv4();
        const { error: saveError } = await supabase
          .from('analyses')
          .insert({
            query,
            result: response.result,
            share_id: newShareId
          });

        if (!saveError) {
          setShareId(newShareId);
        }

        navigate(location.pathname, { replace: true });

        // Refresh search limits after successful analysis
        await refetchLimits();
      }
    } catch (err) {
      console.error('Error submitting query:', err);
    } finally {
      setPendingMessage(null);
      setCurrentMetadata(undefined);
    }
  }, [analyze, setInputValue, recordSearch, user, deepAnalysis, currentMetadata, autoSearch, isAnalyzing, scrollToChat, location.pathname, navigate, refetchLimits, isLimited]);

  const handleClear = () => {
    setReport(null);
    setShareId(null);
    setInputValue('');
    setPendingMessage(null);
    setCurrentMetadata(undefined);
    setHasAttemptedSearch(false);
    ChatHistoryService.clear();
  };

  useEffect(() => {
    if (initialQuery) {
      setInputValue(initialQuery);
      setReport(null);
      setCurrentMetadata(searchMetadata);
      setHasAttemptedSearch(false);
    }
  }, [initialQuery, searchMetadata, setInputValue]);

  useEffect(() => {
    if (autoSearch && inputValue && !isAnalyzing && !report && !hasAttemptedSearch) {
      handleSubmit(inputValue);
    }
  }, [autoSearch, inputValue, isAnalyzing, report, hasAttemptedSearch, handleSubmit]);

  return (
    <>
      <div ref={chatSectionRef} data-chat-section className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500 hidden sm:block" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                What can we help you find?
              </h2>
            </div>
            {(report || isAnalyzing) && (
              <button
                onClick={handleClear}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                title="Clear chat"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="text-sm">Clear</span>
              </button>
            )}
          </div>

          {/* Search Credits Info */}
          <div className="mt-2 text-sm">
            <span className="text-gray-600 dark:text-gray-300">
              {displaySearches !== null ? `${displaySearches} ${user ? 'credits' : 'free searches'} remaining` : 'Loading...'}
            </span>
            {!user && remainingSearches <= 2 && (
              <div className="mt-1">
                <Link to="/signup" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  Sign up to get 10 more free searches!
                </Link>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4">
          <ChatInput 
            onSubmit={handleSubmit} 
            disabled={isAnalyzing}
            pendingMessage={pendingMessage}
          />
        </div>

        {error && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-start gap-3 text-blue-700 dark:text-blue-300" dangerouslySetInnerHTML={{ __html: error }}>
            </div>
          </div>
        )}

        {(isAnalyzing || report) && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            {isAnalyzing ? (
              <AnalysisLoading />
            ) : (
              report && <ChatReport report={report} shareId={shareId} onClear={handleClear} deepAnalysis={deepAnalysis} />
            )}
          </div>
        )}

        <div className="px-4 pb-4 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span>BangaAI can make mistakes. Verify results.</span>
        </div>
      </div>

      {showSignInPrompt && (
        <SignInPrompt onClose={() => setShowSignInPrompt(false)} />
      )}
    </>
  );
};